/**
 * The catalog's URL state, as one type.
 *
 * This is the only representation of "what the user is looking at" in the whole browse
 * experience: `useFilters` reads it out of the query string, the sidebar and the sort control
 * write it back, and the API modules turn it into request parameters. Nothing holds a second copy
 * in component state, which is what makes a filtered view shareable and the back button correct.
 *
 * The parameter *names* deliberately match the API's own (`level`, `skill`, `author`, `sow`,
 * `minHours`, …) so a URL from the address bar can be pasted into `curl` against `/api/v1` with
 * only the host changed.
 */
import type { CourseLevel } from '@catalog/shared';

import type { QueryValue } from '../api/client.js';

export const COURSE_LEVELS = ['beginner', 'intermediate', 'advanced'] as const;

export const COURSE_SORTS = ['relevance', 'title', 'duration', 'recent'] as const;
export type CourseSort = (typeof COURSE_SORTS)[number];

export const SORT_LABELS: Readonly<Record<CourseSort, string>> = {
  relevance: 'Relevance',
  title: 'Title (A–Z)',
  duration: 'Duration',
  recent: 'Recently added',
};

/** 24 per page, per prompt 11. Explicit pages, because a page is linkable and infinite scroll is not. */
export const PAGE_SIZE = 24;

/**
 * The upper bound of the duration slider, in hours.
 *
 * The longest course in the catalog is 55h49m. 60 leaves headroom without making the useful part
 * of the track — most courses are under ten hours — a sliver at the left-hand end.
 */
export const MAX_DURATION_HOURS = 60;

export interface CatalogFilters {
  readonly levels: readonly CourseLevel[];
  readonly skills: readonly string[];
  readonly authors: readonly string[];
  readonly sows: readonly number[];
  readonly knowledgeSuite: string | undefined;
  readonly hasTranscripts: boolean | undefined;
  readonly minHours: number | undefined;
  readonly maxHours: number | undefined;
}

export interface CatalogQuery extends CatalogFilters {
  readonly q: string | undefined;
  readonly sort: CourseSort;
  /** 1-based, because that is what the URL and the paginator both show. */
  readonly page: number;
}

export const EMPTY_FILTERS: CatalogFilters = {
  levels: [],
  skills: [],
  authors: [],
  sows: [],
  knowledgeSuite: undefined,
  hasTranscripts: undefined,
  minHours: undefined,
  maxHours: undefined,
};

/**
 * Relevance only means something when there is a query to be relevant to.
 *
 * Browsing has no ranking signal, so the default there is alphabetical. Deriving the default from
 * `q` rather than storing it keeps `?sort=` out of the URL until the user actually chooses one.
 */
export function defaultSortFor(q: string | undefined): CourseSort {
  return q === undefined ? 'title' : 'relevance';
}

export function hasAnyFilter(filters: CatalogFilters): boolean {
  return (
    filters.levels.length > 0 ||
    filters.skills.length > 0 ||
    filters.authors.length > 0 ||
    filters.sows.length > 0 ||
    filters.knowledgeSuite !== undefined ||
    filters.hasTranscripts !== undefined ||
    filters.minHours !== undefined ||
    filters.maxHours !== undefined
  );
}

/**
 * The filter half of a request, shaped for `toQueryString`.
 *
 * `hasTranscripts` is spelled out rather than coerced: the API enumerates `'true' | 'false'`
 * because `z.coerce.boolean()` is `Boolean(value)`, under which the string `'false'` is `true`.
 */
export function toFilterParams(filters: CatalogFilters): Record<string, QueryValue> {
  return {
    level: filters.levels,
    skill: filters.skills,
    author: filters.authors,
    sow: filters.sows.map((sow) => String(sow)),
    knowledgeSuite: filters.knowledgeSuite,
    hasTranscripts:
      filters.hasTranscripts === undefined ? undefined : String(filters.hasTranscripts),
    minHours: filters.minHours,
    maxHours: filters.maxHours,
  };
}

/**
 * A stable, serializable identity for a filter set — the query-cache key.
 *
 * Arrays are sorted before they are keyed. Without that, ticking "beginner" then "advanced"
 * and ticking them the other way round are two different cache entries for one identical
 * request, and the second one shows a loading state for data already in memory.
 */
export function filterCacheKey(filters: CatalogFilters): Record<string, unknown> {
  return {
    levels: [...filters.levels].sort(),
    skills: [...filters.skills].sort(),
    authors: [...filters.authors].sort(),
    sows: [...filters.sows].sort((a, b) => a - b),
    knowledgeSuite: filters.knowledgeSuite ?? null,
    hasTranscripts: filters.hasTranscripts ?? null,
    minHours: filters.minHours ?? null,
    maxHours: filters.maxHours ?? null,
  };
}
